// Meridian Dark Design System - Breadcrumb Component
// Path navigation with teal chevron separators
// Bloomberg Terminal-inspired executive navigation

import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, Home, MoreHorizontal } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Types
// ============================================================================

export interface BreadcrumbItemProps {
  href?: string;
  current?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  icon?: React.ReactNode;
}

export interface BreadcrumbProps {
  className?: string;
  children: React.ReactNode;
  separator?: React.ReactNode;
  maxItems?: number; // For truncation
  showHome?: boolean;
  homeHref?: string;
}

export interface BreadcrumbSeparatorProps {
  className?: string;
  children?: React.ReactNode;
}

// ============================================================================
// Breadcrumb Container
// ============================================================================

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      className,
      children,
      separator,
      maxItems = 0,
      showHome = false,
      homeHref = '/',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common');
    const childrenArray = React.Children.toArray(children);
    const itemCount = childrenArray.length;

    // Handle truncation for long paths
    let displayedItems: React.ReactNode[] = childrenArray;

    if (maxItems > 0 && itemCount > maxItems) {
      // Keep first item and last (maxItems - 2) items, add ellipsis in between
      if (maxItems > 2) {
        displayedItems = [
          childrenArray[0],
          'ellipsis', // Placeholder for ellipsis
          ...childrenArray.slice(-(maxItems - 2)),
        ];
      } else {
        displayedItems = childrenArray.slice(-maxItems);
      }
    }

    return (
      <nav
        ref={ref}
        className={cn(
          // Base styles
          'flex items-center gap-2 text-sm',
          // Meridian Dark colors
          'text-fg-muted',
          className
        )}
        aria-label={t('accessibility.navigation.breadcrumb')}
        {...props}
      >
        <ol className="flex items-center gap-2">
          {/* Optional Home Icon */}
          {showHome && (
            <>
              <li>
                <Link
                  to={homeHref}
                  className={cn(
                    'flex items-center',
                    'text-fg-muted hover:text-primary transition-all duration-150'
                  )}
                  aria-label={t('accessibility.navigation.home')}
                >
                  <Home className="w-4 h-4" />
                </Link>
              </li>
              {childrenArray.length > 0 && (
                <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>
              )}
            </>
          )}

          {/* Breadcrumb Items */}
          {displayedItems.map((child, index) => {
            const isLast = index === displayedItems.length - 1;

            // Handle ellipsis placeholder
            if (child === 'ellipsis') {
              return (
                <React.Fragment key="ellipsis">
                  <li>
                    <span
                      className={cn('flex items-center px-1', 'text-fg-ghost')}
                      aria-label={t('accessibility.navigation.moreItems')}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </span>
                  </li>
                  <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={`breadcrumb-item-${index}`}>
                <li>{child}</li>
                {!isLast && <BreadcrumbSeparator>{separator}</BreadcrumbSeparator>}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';

// ============================================================================
// Breadcrumb Item
// ============================================================================

export const BreadcrumbItem = React.forwardRef<
  HTMLAnchorElement | HTMLSpanElement,
  BreadcrumbItemProps
>(({ href, current, children, onClick, icon, ...props }, ref) => {
  const content = (
    <>
      {icon && <span className="mr-1">{icon}</span>}
      {children}
    </>
  );

  // Current page - not clickable
  if (current) {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={cn(
          'flex items-center',
          'text-fg font-medium cursor-default',
          'max-w-[200px] truncate'
        )}
        aria-current="page"
        title={typeof children === 'string' ? children : undefined}
        {...props}
      >
        {content}
      </span>
    );
  }

  const mutedClass = cn('flex items-center', 'text-fg-muted', 'max-w-[200px] truncate');
  const title = typeof children === 'string' ? children : undefined;

  // The Bitrix24 iframe is its own app shell: a link out of `/embedded` is
  // bounced by RootLayout to a bare `/embedded/dashboard`, losing the
  // DOMAIN/LANG/APP_SID query and the page state. Read at render, not the
  // module-load IS_EMBEDDED_SESSION constant: that one is frozen for the tab.
  const leavesEmbedded =
    !!href &&
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/embedded') &&
    !href.startsWith('/embedded');

  // Clickable link
  if (href && !leavesEmbedded) {
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={href}
        onClick={onClick}
        className={cn(
          'flex items-center',
          'text-fg-muted hover:text-primary transition-all duration-150',
          'max-w-[200px] truncate'
        )}
        title={title}
        {...props}
      >
        {content}
      </Link>
    );
  }

  // Clickable button
  if (onClick && !href) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        className={cn(
          'flex items-center',
          'text-fg-muted hover:text-primary transition-all duration-150',
          'max-w-[200px] truncate'
        )}
        title={title}
        type="button"
        {...props}
      >
        {content}
      </button>
    );
  }

  // Nothing to do on click: plain text, not a focusable control
  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      className={mutedClass}
      title={title}
      {...props}
    >
      {content}
    </span>
  );
});

BreadcrumbItem.displayName = 'BreadcrumbItem';

// ============================================================================
// Breadcrumb Separator
// ============================================================================

export const BreadcrumbSeparator = React.forwardRef<
  HTMLSpanElement,
  BreadcrumbSeparatorProps
>(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn('text-fg-ghost flex items-center', className)}
      aria-hidden="true"
      {...props}
    >
      {children || <ChevronRight className="w-4 h-4" />}
    </span>
  );
});

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

// ============================================================================
// Utility Components for common patterns
// ============================================================================

export interface BreadcrumbWithTruncationProps extends Omit<BreadcrumbProps, 'children'> {
  items: Array<{
    label: string;
    href?: string;
    current?: boolean;
  }>;
}

// Pre-configured breadcrumb with automatic truncation
export const BreadcrumbWithTruncation = React.forwardRef<
  HTMLElement,
  BreadcrumbWithTruncationProps
>(({ items, maxItems = 4, ...props }, ref) => {
  return (
    <Breadcrumb ref={ref} maxItems={maxItems} {...props}>
      {items.map((item, index) => (
        <BreadcrumbItem
          key={`${item.label}-${index}`}
          href={item.href}
          current={item.current}
        >
          {item.label}
        </BreadcrumbItem>
      ))}
    </Breadcrumb>
  );
});

BreadcrumbWithTruncation.displayName = 'BreadcrumbWithTruncation';

// ============================================================================
// Export all components
// ============================================================================

export default Breadcrumb;
